import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import {
  useAnnotationDraft,
  useAnnotationStrokeColor,
  useAnnotationTool,
  type TextHighlightShape,
} from '@/features/comment-on-file';

interface Options {
  page: number;
  pageElementRef: RefObject<HTMLDivElement | null>;
}

/**
 * When the text-highlight tool is active and the user releases a text
 * selection inside this page, capture it as a TextHighlightShape:
 *   - rects = the selection's client rects intersected with the page box,
 *     converted to fractional 0–1 coords against the rendered page;
 *   - text  = the selected text, clamped to this page (cross-page
 *     selections drop content from other pages).
 *
 * The selection is cleared after capture so the persisted yellow rects
 * aren't visually doubled by the native blue selection box.
 */
export function useTextHighlight({ page, pageElementRef }: Options): void {
  const { tool } = useAnnotationTool();
  const { draftShapes, setDraftShapes } = useAnnotationDraft();
  const { strokeColor } = useAnnotationStrokeColor();

  // Hold mutable values in refs so the pointerup listener can read fresh
  // state without re-attaching on every draft mutation / color change.
  const draftShapesRef = useRef(draftShapes);
  const setDraftShapesRef = useRef(setDraftShapes);
  const strokeColorRef = useRef(strokeColor);
  draftShapesRef.current = draftShapes;
  setDraftShapesRef.current = setDraftShapes;
  strokeColorRef.current = strokeColor;

  useEffect(() => {
    if (tool !== 'text-highlight') return;
    const pageEl = pageElementRef.current;
    if (!pageEl) return;

    const onPointerUp = () => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;
      if (!selection.anchorNode || !pageEl.contains(selection.anchorNode)) return;

      const shape = buildShapeFromSelection(selection, pageEl);
      if (!shape) return;

      setDraftShapesRef.current([
        ...draftShapesRef.current,
        { shape, color: strokeColorRef.current, page },
      ]);
      selection.removeAllRanges();
    };

    pageEl.addEventListener('pointerup', onPointerUp);
    return () => pageEl.removeEventListener('pointerup', onPointerUp);
  }, [tool, page, pageElementRef]);
}

function buildShapeFromSelection(
  selection: Selection,
  pageEl: HTMLElement,
): TextHighlightShape | null {
  const pageRect = pageEl.getBoundingClientRect();
  if (pageRect.width <= 0 || pageRect.height <= 0) return null;

  const rects: TextHighlightShape['rects'] = [];
  const textParts: string[] = [];

  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i);
    const clamped = clampRangeToContainer(range, pageEl);
    if (!clamped) continue;
    textParts.push(clamped.toString());

    for (const cr of clamped.getClientRects()) {
      const left = Math.max(cr.left, pageRect.left);
      const right = Math.min(cr.right, pageRect.right);
      const top = Math.max(cr.top, pageRect.top);
      const bottom = Math.min(cr.bottom, pageRect.bottom);
      const w = right - left;
      const h = bottom - top;
      if (w <= 0 || h <= 0) continue;
      rects.push({
        x: (left - pageRect.left) / pageRect.width,
        y: (top - pageRect.top) / pageRect.height,
        w: w / pageRect.width,
        h: h / pageRect.height,
      });
    }
  }

  if (rects.length === 0) return null;
  const text = textParts.join('').trim();
  if (text.length === 0) return null;

  return { type: 'text-highlight', rects, text };
}

/**
 * Returns a clone of `range` with its boundaries clamped to inside
 * `container`. If neither end of the range is inside the container, the
 * range is treated as not intersecting and `null` is returned.
 */
function clampRangeToContainer(range: Range, container: Element): Range | null {
  const startInside = container.contains(range.startContainer);
  const endInside = container.contains(range.endContainer);
  if (!startInside && !endInside) return null;

  const clone = range.cloneRange();
  if (!startInside) {
    clone.setStart(container, 0);
  }
  if (!endInside) {
    const last = container.lastChild;
    if (last) {
      clone.setEndAfter(last);
    } else {
      clone.setEnd(container, 0);
    }
  }
  return clone;
}
